import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getKiinteistoWhole } from "../api/api";
import { getRadonByPostinumero, updateRakennus } from "../api/api";
// Context
const saveRadonIfMissing = async (data) => {
  const rakennukset =
    data?.rakennukset_fulls ||
    data?.rakennukset ||
    [];

  if (!rakennukset.length) return false;

  const postalCode =
    rakennukset[0]?.postinumero ||
    data?.postinumero;

  if (!postalCode) return false;

  const existingAluetiedot =
    rakennukset[0]?.aluetiedot || {};

  // Everything already exists, including risk-area information
  if (
    existingAluetiedot.radon &&
    existingAluetiedot.kuntaRadon &&
    existingAluetiedot.kuntaRadon.radonRiskialue !== undefined
  ) {
    return false;
  }

  try {
    console.log(
      "📡 Fetching radon for:",
      postalCode
    );

    const res =
      await getRadonByPostinumero(postalCode);

    const postalRadon =
      res.data?.postinumero;

    const municipalityRadon =
      res.data?.kunta;

    const radonRiskialue =
      res.data?.radonRiskialue ?? null;

    if (
      !postalRadon &&
      !municipalityRadon
    ) {
      return false;
    }

    console.log(
      "💾 Saving radon to DB",
      {
        postalRadon,
        municipalityRadon,
        radonRiskialue
      }
    );

    let savedSomething = false;

    for (const r of rakennukset) {
      const currentAluetiedot =
        r.aluetiedot || {};

      const newAluetiedot = {
        ...currentAluetiedot
      };

      let buildingChanged = false;

      // ─────────────────────────────
      // Postal-code radon
      // ─────────────────────────────

      if (
        !currentAluetiedot.radon &&
        postalRadon
      ) {
        newAluetiedot.radon = {
          postinumero:
            postalRadon.Postinumero,

          postitoimipaikka:
            postalRadon.Postitoimipaikka,

          mitattujaAS:
            postalRadon[
              "Mitattuja pientaloasuntoja"
            ],

          keskiarvo:
            postalRadon[
              "Keskiarvo Bq/m3"
            ],

          mediaani:
            postalRadon[
              "Mediaani  Bq/m3"
            ],

          yli200:
            postalRadon[
              "200 Bq/m3 ylitykset %"
            ],

          yli300:
            postalRadon[
              "300 Bq/m3 ylitykset %"
            ],

          yli1000:
            postalRadon[
              "1000 Bq/m3 ylitykset %"
            ],

          source: "STUK"
        };

        buildingChanged = true;
      }

      // ─────────────────────────────
      // Municipality radon
      // ─────────────────────────────

      if (
        municipalityRadon &&
        (
          !currentAluetiedot.kuntaRadon ||
          currentAluetiedot.kuntaRadon.radonRiskialue === undefined
        )
      ) {
        newAluetiedot.kuntaRadon = {
          // Keep existing municipality data if it exists
          ...(currentAluetiedot.kuntaRadon || {}),

          kunta:
            municipalityRadon[
              "Kunta suomeksi"
            ],

          radonRiskialue,

          mitattujaAS:
            municipalityRadon[
              "Mitattuja pientaloasuntoja"
            ],

          keskiarvo:
            municipalityRadon[
              "Keskiarvo Bq/m3"
            ],

          mediaani:
            municipalityRadon[
              "Mediaani Bq/m3"
            ],

          yli200:
            municipalityRadon[
              "200 Bq/m3 ylitykset %"
            ],

          yli300:
            municipalityRadon[
              "300 Bq/m3 ylitykset %"
            ],

          yli1000:
            municipalityRadon[
              "1000 Bq/m3 ylitykset %"
            ],

          source: "STUK"
        };

        buildingChanged = true;
      }

      // Only update DB if something actually changed
      if (buildingChanged) {
        await updateRakennus(
          r.id_rakennus,
          [
            {
              aluetiedot:
                newAluetiedot
            }
          ]
        );

        savedSomething = true;
      }
    }

    return savedSomething;

  } catch (err) {
    console.warn(
      "Radon fetch/save failed:",
      err
    );

    return false;
  }
};
export const RakennusDataContext = createContext({
  rakennusData: null,
  setRakennusData: () => {},
  mergedRakennusData: null,
  setMergedRakennusData: () => {},
  mapCoords: null,            // ✅ add map coordinates
  setMapCoords: () => {},     // ✅ setter
  loading: false,
  error: null,
  refreshRakennusData: async () => {},
});

// Provider
export const RakennusDataProvider = ({ children }) => {
  const { id: kiinteistoId } = useParams();

  const [rakennusData, setRakennusData] = useState(null);
  const [mergedRakennusData, setMergedRakennusData] = useState(null);
  const [mapCoords, setMapCoords] = useState(null);  // ✅ store map coords
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const safeApiCall = async (apiFunc, ...args) => {
    try {
      const res = await apiFunc(...args);
      if (!res || !res.data) throw new Error("No data returned");
      return { data: res.data, error: null, status: res.status || 200 };
    } catch (err) {
      return { data: null, error: err, status: err.response?.status || 500 };
    }
  };

  useEffect(() => {
    console.log("RakennusDataProvider mounted, kiinteistoId:", kiinteistoId);
  }, [kiinteistoId]);

  const fetchRakennusData = useCallback(async () => {
    if (!kiinteistoId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: err } = await safeApiCall(getKiinteistoWhole, kiinteistoId);
let finalData = data;

if (data) {
  const radonWasSaved =
    await saveRadonIfMissing(data);

  if (radonWasSaved) {
    const refreshed =
      await safeApiCall(
        getKiinteistoWhole,
        kiinteistoId
      );

    if (refreshed.data) {
      finalData = refreshed.data;
    }
  }
}

if (err) throw err;

setRakennusData(finalData);

      // ✅ extract coords from the first rakennus
      const coords = finalData?.rakennukset_fulls?.[0]?.sijainti?.coordinates;
      if (coords) {
        setMapCoords([coords[1], coords[0]]); // [lat, lng]
      } else {
        setMapCoords(null);
      }

    } catch (err) {
      console.error("Failed to fetch rakennusData:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [kiinteistoId]);

  useEffect(() => {
    fetchRakennusData();
  }, [fetchRakennusData, kiinteistoId]);

  return (
    <RakennusDataContext.Provider
      value={{
        rakennusData,
        setRakennusData,
        mergedRakennusData,
        setMergedRakennusData,
        mapCoords,          
        setMapCoords,
        loading,
        error,
        refreshRakennusData: fetchRakennusData,
      }}
    >
      {children}
    </RakennusDataContext.Provider>
  );
};

// Custom hook
export const useRakennusData = () => useContext(RakennusDataContext);
